import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const MassachusettsRequirementsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-purple-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                State Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Massachusetts <span className="text-purple-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding 201 CMR 17.00 – one of the nation's strictest state-level data security regulations for protecting personal information.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. State cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for Massachusetts operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Overview */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                  <p className="text-gray-700 text-lg">
                    Massachusetts is one of the most cited examples of a strict statewide cybersecurity rule: <strong>201 CMR 17.00 – Standards for the Protection of Personal Information of Residents of the Commonwealth</strong>. These regulations establish minimum standards for any person or business that owns or licenses "personal information" about Massachusetts residents.
                  </p>
                </div>

                {/* Who is Covered */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Who is Covered?</h2>
                  <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-purple-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Any person or entity engaged in commerce that owns or licenses personal information about a Massachusetts resident in connection with providing goods or services or employment</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-purple-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>This includes many financial institutions, lenders, mortgage companies, and service providers handling MA resident data</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Core Requirements */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Cybersecurity Program Requirements</h2>
                  <p className="text-gray-700 text-lg mb-6">
                    201 CMR 17.00 requires covered entities to implement and maintain a comprehensive written information security program (WISP) that is:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-file-shield-line text-purple-600"></i>
                        Customized and Risk-Based
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>"Appropriate to the size, scope, and type of business"</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Takes into account the amount and sensitivity of personal information, the need for security, and the business's resources</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-admin-line text-purple-600"></i>
                        Administrative Safeguards
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Designate one or more employees to maintain the security program</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Perform ongoing risk assessment of internal and external threats</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Develop policies for storage, access, and transportation of personal information</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Impose disciplinary measures for violations</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Oversee service providers and require contractual safeguards</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Review the WISP at least annually or when there is a material change in business practices</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-code-s-slash-line text-purple-600"></i>
                        Technical Safeguards
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Secure user authentication (password policies, access controls)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Unique IDs and access logging</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Encryption of personal information transmitted over public networks and, to the extent technically feasible, on portable devices and in certain storage contexts</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Up-to-date firewall protection and security patches</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Malware protection and system monitoring for unauthorized use</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Reasonable system security for wireless networks</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-building-line text-purple-600"></i>
                        Physical Safeguards
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Restrict physical access to records containing personal information</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                          <span>Secure storage, destruction, and disposal practices</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Breach and Enforcement */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Breach and Enforcement</h2>
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>201 CMR 17.00 ties into Massachusetts' broader data breach law, which requires notification to affected residents and the Attorney General in the event of a security breach involving personal information</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Non-compliance can be treated as an unfair or deceptive practice under Massachusetts consumer protection law, potentially leading to enforcement actions</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-purple-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help with Massachusetts Compliance?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team helps businesses understand and implement the controls needed to meet 201 CMR 17.00 requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-md whitespace-nowrap cursor-pointer">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-purple-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MassachusettsRequirementsPage;
